<script>
  /**
   * Account creation for invited users.
   *
   * The invitation email links here with ?email= prefilled. There is no
   * acceptance token: the grant is claimed server-side by
   * auth_hooks.on_user_created when someone registers with an address that has
   * a pending invitation. That means the address must match exactly, which is
   * why it is prefilled and called out below rather than left to memory.
   */
  import { useRouter } from '@dvcol/svelte-simple-router';
  import api from '$lib/api.js';
  import { authStore } from '../stores/authStore.svelte.js';

  const router = useRouter();

  const invitedEmail = new URLSearchParams(window.location.search).get('email');

  let email = $state(invitedEmail ?? '');
  let firstName = $state('');
  let lastName = $state('');
  let password = $state('');
  let confirm = $state('');
  let termsAccepted = $state(false);
  let loading = $state(false);
  let error = $state(null);

  const validEmail = $derived(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
  const longEnough = $derived(password.length >= 8);
  const matches = $derived(password.length > 0 && password === confirm);
  const canSubmit = $derived(validEmail && longEnough && matches && termsAccepted && !loading);

  async function submit(event) {
    event.preventDefault();
    if (!canSubmit) return;
    loading = true;
    error = null;

    try {
      await api.register({
        email,
        password,
        first_name: firstName || null,
        last_name: lastName || null,
        terms_accepted: termsAccepted,
      });

      // Registration does not return a session, so sign in explicitly. The
      // invitation was already claimed by the on_user_created hook, so the
      // context call below should now find a membership.
      const result = await api.login(email, password);
      authStore.setAuth(result.user, result.access_token);

      try {
        await authStore.loadContext();
      } catch (contextError) {
        if (contextError.status === 403) {
          // Account created, but no invitation matched this address.
          authStore.clearAuth();
          error = 'Your account was created, but it has no portal access yet. '
                + 'Make sure you used the exact address your invitation was sent to, '
                + 'or ask your property manager to invite this address.';
          return;
        }
        throw contextError;
      }

      router.push({ name: 'dashboard' });
    } catch (e) {
      error = e.message || 'Could not create your account';
    } finally {
      loading = false;
    }
  }
</script>

<div class="flex min-h-screen items-center justify-center px-4 py-10">
  <div class="w-full max-w-sm">
    <div class="mb-8 text-center">
      <i class="fas fa-house-chimney text-2xl" style="color: var(--p-brand)" aria-hidden="true"></i>
      <h1 class="mt-3 text-lg font-semibold">Create your account</h1>
    </div>

    <form class="p-card space-y-4 p-6" onsubmit={submit}>
      {#if error}
        <p class="rounded-lg px-3 py-2 text-sm"
           style="background-color: var(--p-danger-bg); color: var(--p-danger)" role="alert">
          {error}
        </p>
      {/if}

      <div>
        <label for="email" class="text-xs font-medium p-muted">Email</label>
        <input id="email" class="p-input mt-1" type="email" bind:value={email}
               autocomplete="email" required />
        {#if invitedEmail}
          <p class="mt-1 text-xs p-faint">
            Your invitation was sent to this address. Changing it means your access
            will not be applied.
          </p>
        {/if}
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div>
          <label for="first" class="text-xs font-medium p-muted">First name</label>
          <input id="first" class="p-input mt-1" bind:value={firstName} autocomplete="given-name" />
        </div>
        <div>
          <label for="last" class="text-xs font-medium p-muted">Last name</label>
          <input id="last" class="p-input mt-1" bind:value={lastName} autocomplete="family-name" />
        </div>
      </div>

      <div>
        <label for="password" class="text-xs font-medium p-muted">Password</label>
        <input id="password" class="p-input mt-1" type="password" bind:value={password}
               autocomplete="new-password" required />
        {#if password && !longEnough}
          <p class="mt-1 text-xs" style="color: var(--p-danger)">
            Use at least 8 characters.
          </p>
        {/if}
      </div>

      <div>
        <label for="confirm" class="text-xs font-medium p-muted">Confirm password</label>
        <input id="confirm" class="p-input mt-1" type="password" bind:value={confirm}
               autocomplete="new-password" required />
        {#if confirm && !matches}
          <p class="mt-1 text-xs" style="color: var(--p-danger)">Passwords do not match.</p>
        {/if}
      </div>

      <label class="flex items-start gap-2 text-xs p-muted">
        <input type="checkbox" class="mt-0.5" bind:checked={termsAccepted} />
        <span>I accept the Terms of Service.</span>
      </label>

      <button type="submit" class="p-btn p-btn-primary w-full justify-center" disabled={!canSubmit}>
        {#if loading}
          <span class="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></span>
        {/if}
        Create account
      </button>

      <a href="/strao/login" class="block text-center text-xs p-muted hover:underline">
        Already have an account? Sign in
      </a>
    </form>
  </div>
</div>
